import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import qs from 'qs';
import firebase from 'firebase/app';
import { Typography } from 'antd';
import { Redirect, useLocation, useHistory } from 'react-router-dom';

import ClientGoogleLogin from '../../../components/client/ClientGoogleLogin';
import ClientEmailLogin from '../../../components/client/ClientEmailLogin';
import { formatE164ForDisplay, countryForCurrency, nationalFromE164 } from '../../../utils/phone';
import ClientPhoneLogin from '../../../components/client/ClientPhoneLogin';
import ClientAppleLogin from '../../../components/client/ClientAppleLogin';
import ClientIdentityFirst from '../../../components/client/ClientIdentityFirst';
import PhoneFirstSignup from '../../../components/client/PhoneFirstSignup';
import { signupTextLink } from '../../../components/client/SignupShell';
import CustomerOnlyNotice from '../../../components/client/CustomerOnlyNotice';
import LoginContextBanner from '../../../components/client/LoginContextBanner';
import LoginHeading from '../../../components/client/LoginHeading';
import LoginModeSwitcher from '../../../components/client/LoginModeSwitcher';
import TrackerService from '../../../utils/TrackerService';
import {
  ORIGIN_REASON_MAP,
  encodeOriginParams,
  decodeOriginParams,
  buildOriginContext,
  buildQueryString,
  inferOriginReason,
} from '../../../utils/loginOrigin';
import { consumePendingAuthIntent } from '../../../utils/authFlow';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useUser } from '../../../models/useUser';
import { useClientForOwner } from '../../../models/useClientForOwner';
import { useLoginContext } from '../../../models/useLoginContext';
import { findExistingAccountFor, recaptchaExpected, describeProviders } from '../../../utils/accountLookup';

import '../../../App.scss';

const { Text } = Typography;

const GOOG_RECAPTCHA_SITE_KEY = '6LdZWSYrAAAAAIeT9OanpWKafZysLd_gMis6EgD8';
const SESSION_STORAGE_REDIRECT_KEY = 's_login_redirect';
const SESSION_STORAGE_REDIRECT_ROUTE_KEY = 's_login_redirect_route';
const SESSION_STORAGE_TIMESTAMP_KEY = 's_login_timestamp';
const SESSION_STORAGE_SIGNUP_ORIGIN_KEY = 's_signup_origin_v1';
const REDIRECT_LIFETIME_MS = 15 * 60 * 1000;

// ── Established-account guard (Aug 2026 lockouts) ──
// An auth account older than this predates the current sign-in attempt entirely, so
// it cannot be debris of an interrupted signup — it is somebody's existing identity.
// Established accounts are healed (role reattached), never asked, and NEVER deleted:
// the only deletable identity is one minted moments ago. Unknown age fails safe.

// The roles-stamping era began with the intent gate + parking (2026-07-29). A role-less
// account created BEFORE then is a genuine pre-attribution veteran — a client by
// definition (customers never signed in here in that era). A role-less account created
// AFTER is a new-era artifact — an abandoned shell, a stranded signup, or someone
// who authenticated on a surface with no signup UI at all (the /admin/login employee
// Google button, the Stripe-app OAuth screen — the intent gate's warn log names
// these). Customer signup exists ONLY on the strike-payer webapp and always stamps
// roles:['customer'], so customers never appear role-less. Consent is a live question:
// it gets the ask-first prompt, never a silent client role. Unknown age → prompt (never
// grant a role on a guess). Deletion no longer exists client-side (2026-08-13).
export const ROLE_STAMPING_ERA_START_MS = Date.parse('2026-07-29T00:00:00Z');
export function isPreAttributionVeteran(authUser) {
  const createdAtMs = Date.parse(authUser?.metadata?.creationTime || '');
  if (!Number.isFinite(createdAtMs)) return false;
  return createdAtMs < ROLE_STAMPING_ERA_START_MS;
}


/** Clears all login/claim redirect session vars. */
export function clearSessionForDashboard() {
  sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_ROUTE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_TIMESTAMP_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_SIGNUP_ORIGIN_KEY);
}

export { inferOriginReason, buildOriginContext };

// ---------------------------------------------------------------------------
// Session storage helpers (screen-specific, not reusable)
// ---------------------------------------------------------------------------

function readStoredOriginContext() {
  const raw = sessionStorage.getItem(SESSION_STORAGE_SIGNUP_ORIGIN_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (error) { return null; }
}
function writeStoredOriginContext(context) {
  if (!context || !context.reason) return;
  sessionStorage.setItem(SESSION_STORAGE_SIGNUP_ORIGIN_KEY, JSON.stringify(context));
}

function sessionSetRedirectParams(redirect, redirectRoute) {
  if (redirect) {
    sessionStorage.setItem(SESSION_STORAGE_REDIRECT_KEY, redirect);
    sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_ROUTE_KEY);
  }
  if (redirectRoute) {
    sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_KEY);
    sessionStorage.setItem(SESSION_STORAGE_REDIRECT_ROUTE_KEY, redirectRoute);
  }
  if (redirect || redirectRoute) {
    sessionStorage.setItem(SESSION_STORAGE_TIMESTAMP_KEY, new Date().getTime().toString());
  }
}
function sessionClearRedirectParams() {
  sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_ROUTE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_TIMESTAMP_KEY);
}

function resolveQueryParams(rawSearch) {
  const passedQueryParams = qs.parse(rawSearch, { ignoreQueryPrefix: true }) || {};
  let updated = { ...passedQueryParams };
  const { redirect, redirectRoute } = passedQueryParams;
  const storedRedirect = sessionStorage.getItem(SESSION_STORAGE_REDIRECT_KEY);
  const storedRedirectRoute = sessionStorage.getItem(SESSION_STORAGE_REDIRECT_ROUTE_KEY);
  const storedTimestamp = sessionStorage.getItem(SESSION_STORAGE_TIMESTAMP_KEY);
  const isStored = storedRedirect || storedRedirectRoute;
  const isValidTime = storedTimestamp
    && (new Date().getTime() - parseInt(storedTimestamp, 10) < REDIRECT_LIFETIME_MS);

  if (redirect || redirectRoute) {
    sessionSetRedirectParams(redirect, redirectRoute);
  } else if (isStored && isValidTime) {
    if (storedRedirect) updated.redirect = storedRedirect;
    if (storedRedirectRoute) updated.redirectRoute = storedRedirectRoute;
  } else {
    sessionClearRedirectParams();
  }

  // Which door this is — decided by the link in the address bar only. Neither the
  // 15-minute redirect carriage above nor the stored first-touch attribution below
  // may turn a later plain /client/login (the root door) into a sign-up: both
  // outlive the link, and the root is where people come back to log in.
  updated.doorReason = inferOriginReason({ redirect, redirectRoute });

  const storedCtx = readStoredOriginContext();
  const resolved = buildOriginContext(updated, storedCtx);
  writeStoredOriginContext(resolved);
  updated.originReason = resolved.reason;
  updated.originPath = resolved.path;
  updated.originParams = encodeOriginParams(resolved.params);
  return updated;
}

/**
 * Builds origin context from URL params only (no sessionStorage).
 * Used for display context — landing on /client/login with no params shows generic UI.
 */
function buildDisplayOriginContext(rawSearch) {
  const urlParams = qs.parse(rawSearch, { ignoreQueryPrefix: true }) || {};
  const resolved = buildOriginContext(urlParams, null);
  return {
    reason: resolved.reason,
    path: resolved.path,
    params: resolved.params,
  };
}

/**
 * Which mode a door opens on. A claim or join link is an invitation, so those
 * doors lead with sign-up and keep "Already have an account? Log in" one tap
 * away. The root (/client/login from the app, a typed address) is where people
 * come back, so it leads with login and keeps sign-up one tap away.
 */
export function doorLeadsWithSignup(originReason) {
  return originReason === ORIGIN_REASON_MAP.JOIN_TEAM || originReason === ORIGIN_REASON_MAP.CLAIM_TAG;
}

/**
 * A users doc the person disowned ("use my existing account") that never got the
 * client role back. Read once, at sign-in; a missing doc is not a shell.
 */
async function isDisownedShell(uid) {
  try {
    const snap = await firebase.firestore().collection('users').doc(uid).get();
    if (!snap || !snap.exists) return false;
    const data = snap.data() || {};
    const roles = Array.isArray(data.roles) ? data.roles : [];
    return !!data.declinedAsDuplicateOf && !roles.includes('client');
  } catch (err) {
    return false;
  }
}

function assertCompleteClientStatus(client) {
  if (!client) return false;
  const { agreeTerms } = client;
  // Identity is verified if email was verified, OR if phone auth was used (OTP = verified),
  // OR Apple (Firebase Auth sets emailVerified=true for Apple users).
  const isVerified = !!(client.emailVerified || client.phoneNumber);
  // A valid identifier is either an email address or a phone number.
  const hasIdentifier = !!(client.email || client.phoneNumber);
  let required = ['displayName', 'agreeTerms'];
  if (!agreeTerms) required = ['dob', 'country', ...required];
  for (const prop of required) {
    if (!client.hasOwnProperty(prop) || client[prop] == null) return false;
  }
  return !!(agreeTerms && isVerified && hasIdentifier);
}

const LOCALE_STRINGS = {
  en: {
    terms_and_conditions: "TERMS",
    privacy_policy: "PRIVACY",
    paymentcard2_cashlesstippingtext_46302: "Powered by ",
    title: { signup: "Open your new account", login: "Welcome back" },
    subtitle: { signup: "Sign up with your mobile number", login: "Log in with Google or email" },
    subsubtitle: { signup: "It takes about a minute.", login: "Manage your balance and account" },
    switchtext: { signup: "Already have an account?", login: "Don't have an account?" },
    switchlink: { signup: "Log in", login: "Sign up" },
  }
};

// ---------------------------------------------------------------------------
// resolveNextRoute — the single decision tree
//
// Inputs (all from live subscriptions):
//   authUser        — Firebase Auth user (or null)
//   firestoreUser   — Firestore /users/{uid} document (or null)
//   client          — Firestore /clients where ownerId==uid (or null)
//   emailVerified   — derived boolean
//   queryParams     — parsed URL + session storage redirect context
//
// Returns { route, clearSession, message } or null (= not enough data yet)
// ---------------------------------------------------------------------------
function resolveNextRoute({ emailVerified, firestoreUser, client, queryParams }) {
  const { redirect, redirectRoute } = queryParams;

  // ── Step 1: email not verified → verify-email screen. ──
  // Deliberately NOT gated on isHuman: the reCAPTCHA/isHuman check must never stand
  // between a signed-in user and being told to verify their email — a stalled
  // grecaptcha used to park new signups on an endless spinner here
  // ("waiting_for_human" lockout, found via a client video 2026-07-22). Email
  // verification is itself the bot gate on this path, and provisioning fires on
  // EITHER isHuman OR emailVerified flipping true (userToClient.js), so nothing
  // downstream needs isHuman first.
  if (!emailVerified) {
    const qs = redirect ? buildQueryString({ redirect })
      : redirectRoute ? buildQueryString({ redirectRoute }) : '';
    return { route: `/client/verify-email${qs}`, clearSession: false, message: 'Please verify your email' };
  }

  // ── Step 2: no client doc yet → null (keep waiting) ──
  if (!client) {
    // But first: customer-only check
    if (firestoreUser) {
      const roles = firestoreUser.roles || [];
      if (roles.includes('customer') && !roles.includes('client')) {
        return { route: null, clearSession: false, message: 'customer_only' };
      }
    }
    return null;
  }

  // ── Step 3: client exists but paused → home page ──
  if (client.status === 'paused') {
    return { route: '/', clearSession: true, message: 'Account paused' };
  }

  // ── Step 4: client exists but incomplete → complete signup ──
  const isComplete = assertCompleteClientStatus({ ...client, emailVerified: true });
  if (!isComplete) {
    const qr = redirect ? buildQueryString({ redirect })
      : redirectRoute ? buildQueryString({ redirect: `/client/manage/${client.id}/${redirectRoute}` }) : '';
    return { route: `/client/completeSignup/${client.id}${qr}`, clearSession: true, message: 'Complete your account' };
  }

  // ── Step 5: client is complete — route based on redirect context ──
  // (b) Claim flow
  if (redirect) {
    return { route: redirect, clearSession: true, message: 'Redirecting…' };
  }

  // (c) Join team / sub-route
  if (redirectRoute) {
    return { route: `/client/manage/${client.id}/${redirectRoute}`, clearSession: true, message: 'Redirecting…' };
  }

  // (d) Dashboard
  return { route: `/client/manage/${client.id}`, clearSession: true, message: 'Going to dashboard' };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ClientLoginScreen(props) {
  const location = useLocation();
  const history = useHistory();
  const isLocalHost = window.location.hostname === 'localhost';

  // ── 1. Query params (stable for mount lifetime) ──
  const queryParams = useMemo(
    () => resolveQueryParams(location.search),
    [] // eslint-disable-line
  );

  // ── Locale ──
  const locStrings = useMemo(() => {
    const LOCALE = props.locStrings?.locale || 'en';
    const base = LOCALE_STRINGS[LOCALE] || LOCALE_STRINGS.en;
    return { ...base, ...(props.locStrings || {}) };
  }, [props.locStrings]);

  // ── UI state ──
  // ?mode=login overrides a link door's sign-up lead: the About-you step sends a
  // phone-first person here who says they already have an email account, with
  // the join/claim kept in ?redirect. ?entry=email opens the email field at once.
  const [isSignup, setIsSignup] = useState(
    location.pathname === '/client/signup'
      || (doorLeadsWithSignup(queryParams.doorReason) && queryParams.mode !== 'login'),
  );
  const [message, setMessage] = useState(null);
  // True when the post-sign-in spinner has run >20s — surfaces a reload CTA so no
  // environment (blocked reCAPTCHA, dead network) ever leaves users spinning forever.
  const [loadingStalled, setLoadingStalled] = useState(false);
  // True once getRedirectResult has resolved (or is unavailable). The verifyHuman
  // fallback waits for this so it never runs against a redirect-return uid that the
  // anti-duplicate abort is about to delete.
  const [redirectSettled, setRedirectSettled] = useState(false);
  const [redirectOnLogin, setRedirectOnLogin] = useState(null);
  const [isCustomerOnly, setIsCustomerOnly] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  // Whichever identifier the user typed on the entry screen becomes the "open" method;
  // the other collapses behind a single button. Email is open by default (e.g. no lookup).
  const [showEmail, setShowEmail] = useState(true);
  // ── Identity-first flow state ──
  // lookupResult: { status, providers, hasPhoneCredential, identifier } | null
  // showAllProviders: escape hatch / create-new path that reveals every provider.
  const [lookupResult, setLookupResult] = useState(null);
  const [showAllProviders, setShowAllProviders] = useState(false);
  // Anti-duplicate-account gate: on a not_found lookup we show an interstitial first;
  // the user must explicitly choose to create a new account before we reveal sign-up.
  const [proceedToSignup, setProceedToSignup] = useState(false);
  // Visible (prod) error for the provider step — e.g. the "existing means existing" abort.
  const [authError, setAuthError] = useState(null);
  // ── Step-1 chooser state ──
  // Login: the first screen lists all methods (Google, Apple, mobile, email). Picking
  // mobile or email reveals the identity field in that mode (identityEntry: 'phone' |
  // 'email' | null) — two explicit choices, so the mobile entry shows its country
  // picker from the start instead of guessing from the first digits typed. Signup:
  // leads with Google + Apple; "More options" reveals email (signupMoreOptions).
  const [identityEntry, setIdentityEntry] = useState(
    queryParams.entry === 'email' || queryParams.entry === 'phone' ? queryParams.entry : null,
  );
  // Country of the number tried last, so "Try again" / "Use a different number"
  // reopen the picker where the person left it rather than on the page default.
  const [retryCountry, setRetryCountry] = useState(null);
  const [signupMoreOptions, setSignupMoreOptions] = useState(false);
  // Found-account screen: the one method shown open. null = the one matching
  // what they typed (phone → phone, email → email), else the account's first.
  const [scopedMethod, setScopedMethod] = useState(null);

  // ── SUBSCRIPTION 1: Firebase Auth user (single source of login truth) ──
  const [authLoading, authUser] = useCurrentUser();
  const uid = authUser && !authUser.isAnonymous ? authUser.uid : null;

  // ── SUBSCRIPTION 2: Firestore /users/{uid} ──
  const [userLoading, firestoreUser] = useUser(uid);

  // ── SUBSCRIPTION 3: Firestore /clients where ownerId == uid ──
  const [clientLoading, client] = useClientForOwner(uid);

  // ── Derived emailVerified (auth OR Firestore) ──
  // Phone auth is verified by OTP; Apple sets emailVerified=true in Firebase Auth.
  // Either condition counts as a verified identity for routing purposes.
  const emailVerified = !!(authUser?.emailVerified)
    || !!(firestoreUser?.emailVerified)
    || !!(authUser?.phoneNumber);

  // ── Mutable refs ──
  const signupInProgressRef = useRef(false);
  const verifyHumanAttemptedRef = useRef(null);
  // The decision-tree effect re-runs on every auth/user/client snapshot (new object
  // refs), so the sign-in analytics must be de-duped or it fires once per Firestore
  // update — inflating client_sign_in into 100k+/day spikes. Fire at most once per uid.
  const signInTrackedRef = useRef(null);
  // The decision-tree effect bails while a sign-in/signup handler owns the flow
  // (signupInProgressRef). Clearing a ref doesn't re-render, so if the auth/user/client
  // subscriptions all settle DURING the handler, the redirect would compute once, get
  // skipped, and never retry — leaving the user stuck on the login screen. Bump this state
  // when the handler finishes to force the effect to re-run.
  const [signupSettledTick, setSignupSettledTick] = useState(0);
  const endSignupFlow = useCallback(() => {
    signupInProgressRef.current = false;
    setSignupSettledTick((t) => t + 1);
  }, []);
  // ── Origin context for redirect logic (includes sessionStorage) ──
  const originContext = useMemo(() => ({
    reason: queryParams.originReason || ORIGIN_REASON_MAP.WEBSITE,
    path: queryParams.originPath || '/client/login',
    params: decodeOriginParams(queryParams.originParams),
  }), [queryParams]);

  // ── Landing context (drives the ask-first copy) ──
  // EVERY entry point serves two first-class signed-out populations: brand-new people
  // and existing users signing back in. So no landing may presume either — a provider
  // sign-in that mints a NEW auth user is never auto-deleted; the user is asked first
  // (ask-first interstitial below). Derived from the URL (redirect/redirectRoute),
  // which survives the OAuth redirect round-trip — no storage involved.
  //
  // Production evidence for making this universal (2026-07-29): a new merchant landing
  // on bare /client/login from the marketing site tapped "Sign in with Apple", was
  // deleted by the strict abort, retried 3× (each a fresh uid + a pause-swept client),
  // hit the reCAPTCHA score floor ("Bot?") and never got an account.
  const landingReason = useMemo(() => inferOriginReason(queryParams), [queryParams]);
  const isJoinTeamLanding = landingReason === ORIGIN_REASON_MAP.JOIN_TEAM;
  const isClaimTagLanding = landingReason === ORIGIN_REASON_MAP.CLAIM_TAG;
  // Partner-integration joins carry an xmid on the join link
  // (/client/join/{tagId}?xmid=...) — accepting connects an external system's member
  // ID (e.g. OrbXi) to the team membership. Say so explicitly in the ask-first copy.
  const isPartnerConnectLanding = useMemo(
    () => isJoinTeamLanding
      && /[?&]xmid=/i.test(`${queryParams.redirect || ''} ${queryParams.redirectRoute || ''}`),
    [isJoinTeamLanding, queryParams]
  );
  // A provider sign-in minted a brand-new auth user with no signup intent. Instead of
  // deleting it outright, park it here and ask:
  // { authResult, via: 'signup' | 'verified' } — via picks the resume handler.
  const [pendingNewIdentity, setPendingNewIdentity] = useState(null);

  // ── Display context: URL only, no sessionStorage. /client/login with no params = generic UI. ──
  const displayOriginContext = useMemo(
    () => buildDisplayOriginContext(location.search),
    [location.search]
  );
  const loginContext = useLoginContext(displayOriginContext);

  // ── After email verification OOB redirect: auth token can be stale (emailVerified still false).
  // Reload user so we get the updated state and the decision tree can redirect correctly.
  const authReloadAttemptedRef = useRef(false);
  useEffect(() => {
    if (!authUser || authUser.emailVerified || authReloadAttemptedRef.current) return;
    authReloadAttemptedRef.current = true;
    authUser.reload()
      .then(() => authUser.getIdToken(true))
      .catch((err) => {
        console.warn('[login] auth reload failed:', err?.message);
        authReloadAttemptedRef.current = false;
      });
  }, [authUser]);

  // ── Write user metadata once on login. Triggers backend onUpdateUser.
  // Backend syncs emailVerified from Firebase Auth (source of truth) via syncUserIdentityFromAuth.
  // We do NOT write emailVerified or isHuman — those are external state.
  const metadataWrittenRef = useRef(null);
  useEffect(() => {
    if (!uid || metadataWrittenRef.current === uid) return;
    metadataWrittenRef.current = uid;
    // NO roles grant here: this effect fires for ANY signed-in uid, including
    // transient ones about to be deleted by abortIfAccidentalNewUser. Granting
    // 'client' from here let the backend provision a client for uids with no
    // consented signup (2026-07-27 duplicate-clients incident). The role is
    // written only by the post-abort success handlers below.
    const updates = {
      platform: 'web',
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      lastAuthAt: firebase.firestore.FieldValue.serverTimestamp(),
      lastOriginReason: originContext.reason || ORIGIN_REASON_MAP.WEBSITE,
      lastOriginPath: originContext.path || '/client/login',
      lastOriginParams: originContext.params || {},
      lastOriginBase: window.location.origin,
    };
    const { redirect: redir, redirectRoute: redirRoute } = queryParams;
    if (redir) updates.pendingRedirect = redir;
    else if (redirRoute) updates.pendingRedirectRoute = redirRoute;
    if (navigator.language) updates.locale = navigator.language;
    if (authUser?.displayName) updates.displayName = authUser.displayName;
    if (authUser?.email) updates.email = authUser.email;
    firebase.firestore().collection('users').doc(uid)
      .set(updates, { merge: true })
      .catch(err => console.warn('[login] metadata write failed:', err?.message));
  }, [uid, authUser, originContext, queryParams]);

  // ── Ask-first: is there an account OTHER than the one just signed in? ──
  // The parked screen used to assert "no existing striketip account found" without
  // ever looking. Someone who signed up by mobile two venues ago, with their Gmail
  // on the profile, tapped Google, read that, and made the duplicate in one tap —
  // "Continue" was the primary button. Run the same lookup the typed path uses, on
  // the identity's email/phone, and let the result pick the screen. The server
  // skips the caller's own uid (and the client guards against finding itself), so
  // the orphan can't match itself. Couldn't check = the old screen; never a lockout.
  const checkParkedIdentityForExistingAccount = useCallback(async (authResult) => {
    const match = await findExistingAccountFor(authResult && authResult.user);
    setPendingNewIdentity((prev) => (
      prev && prev.authResult === authResult ? { ...prev, checking: false, match } : prev
    ));
  }, []);

  // ── Resume ask-first: verified human, no client doc, no 'client' role ──
  // A settled, signed-in, verified identity with no client and no client role is one
  // of two things: (a) a brand-new identity whose ask-first choice was lost — page
  // reload, webview killed mid-question — or (b) a signup stranded before its role
  // write (2026-07-27 incident victims, transient write failures). Both get the SAME
  // treatment: show the ask-first choice again (via:'resume', no authResult).
  //
  // This effect used to WRITE roles:['client'] silently here ("self-heal"). That
  // silently signed up anyone who reloaded mid-choice — bypassing the consent the
  // ask-first screen exists to collect, and (post intent-gate) minting the client
  // the gate was built to withhold. Consent is now explicit on every path: the only
  // client-role writers are the post-consent handlers and confirmNewIdentity.
  const resumeAskAttemptedRef = useRef(null);
  const veteranHealAttemptedRef = useRef(null);
  useEffect(() => {
    const subscriptionsSettled = !!uid && !userLoading && !clientLoading;
    // A shell the person disowned ("Sign in with my existing account") keeps its
    // backend-provisioned client doc, so a client existing no longer means resolved
    // for it: a later tap on the same Google/Apple button lands here and must ask
    // again rather than auto-complete the shell as a second account.
    const disownedShell = !!(firestoreUser && firestoreUser.declinedAsDuplicateOf);
    const alreadyResolved = (!!client && !disownedShell) || isCustomerOnly;
    if (!subscriptionsSettled || alreadyResolved) return;
    if (!redirectSettled || signupInProgressRef.current || pendingNewIdentity) return;
    if (!firestoreUser || firestoreUser.isHuman !== true || !emailVerified) return;
    // Never resume for a tombstoned doc. abortIfAccidentalNewUser deletes the auth
    // user and onDeleteAuthUser marks the doc deleted:true — but the auth-state
    // change can lag the delete, leaving a window where this effect still sees the
    // dying uid (observed in production 2026-07-29).
    if (firestoreUser.deleted === true) return;
    const currentRoles = Array.isArray(firestoreUser.roles) ? firestoreUser.roles : [];
    if (currentRoles.includes('client')) return;
    if (resumeAskAttemptedRef.current === uid) return;
    resumeAskAttemptedRef.current = uid;
    if (isPreAttributionVeteran(authUser)) {
      // Pre-attribution veteran (account predates the roles-stamping era): old users
      // docs were never stamped with roles, and in that era every account on this
      // surface was a client. Reattach the role instead of asking — the ask-first
      // prompt exists to collect consent from a BRAND-NEW identity, and its reject
      // path discards the signed-in account. Shown to a veteran, "I have another
      // account" read as an honest answer and deleted their years-old sign-in
      // (Aug 2026 lockouts). A role-less account created AFTER the era cutoff is
      // NOT healed — it never expressed client intent (an /admin/login employee
      // sign-in, a Stripe-app OAuth login, an abandoned signup) — it falls through
      // to the prompt below, where the established-delete guard already makes
      // reject sign-out-only.
      if (veteranHealAttemptedRef.current === uid) return;
      veteranHealAttemptedRef.current = uid;
      firebase.firestore().collection('users').doc(uid).set({
        roles: firebase.firestore.FieldValue.arrayUnion('client'),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true }).catch((err) => console.warn('[login] veteran role reattach failed:', err?.message));
      return;
    }
    // Resume parks with the signed-in user as the identity, and looks for an
    // existing account for it the same way a fresh park does — the second pass
    // must not assert "no existing account" either.
    const resumed = { authResult: authUser ? { user: authUser } : null, via: 'resume', checking: false };
    const identifier = authUser && (authUser.email || authUser.phoneNumber);
    if (identifier && recaptchaExpected()) resumed.checking = true;
    setPendingNewIdentity(resumed);
    if (resumed.checking) checkParkedIdentityForExistingAccount(resumed.authResult);
  }, [uid, userLoading, clientLoading, client, isCustomerOnly, firestoreUser, authUser,
    emailVerified, redirectSettled, signupSettledTick, pendingNewIdentity,
    checkParkedIdentityForExistingAccount]);

  // ── Fallback: run verifyHuman when uid exists but isHuman is not set
  // FirebaseUI signInSuccessWithAuthResult may never fire (e.g. popup closes, component unmounts).
  // This ensures verifyHuman runs regardless.
  useEffect(() => {
    if (!uid || userLoading || isCustomerOnly) return undefined;
    if (firestoreUser?.isHuman === true) return undefined;
    // Wait until sign-in has settled: while a redirect result is pending, a
    // signup handler owns the flow, or an invite ask-first choice is open, this
    // uid may be about to be deleted by abortIfAccidentalNewUser — hammering
    // reCAPTCHA for it both wastes attempts and degrades the score for the
    // user's REAL attempt ("Bot?" lockouts).
    if (!redirectSettled || signupInProgressRef.current || pendingNewIdentity) return undefined;

    // Bounded retry with backoff. The old version gave up SILENTLY (no ref reset) when
    // grecaptcha hadn't loaded or returned no token — leaving the user on an endless
    // spinner with no retry for the whole page session. Every failure path now retries
    // (2s/4s/8s), and the attempt counter is per-uid so a new sign-in starts fresh.
    const state = verifyHumanAttemptedRef.current;
    if (!state || state.uid !== uid) {
      verifyHumanAttemptedRef.current = { uid, count: 0, timer: null };
    }
    const attemptState = verifyHumanAttemptedRef.current;
    let cancelled = false;

    const attempt = async () => {
      if (cancelled || attemptState.count >= 4) return;
      attemptState.count += 1;
      try {
        await new Promise((resolve) => window.grecaptcha?.ready?.(resolve) ?? resolve());
        if (!window.grecaptcha?.execute) throw new Error('grecaptcha not loaded');
        const token = await window.grecaptcha.execute(GOOG_RECAPTCHA_SITE_KEY, { action: 'signup' });
        if (!token) throw new Error('no reCAPTCHA token');
        const verifyHuman = firebase.functions().httpsCallable('userService-humanHandler-verifyHuman');
        await verifyHuman({ token, action: 'signup', platform: 'web' });
        // Success: users/{uid}.isHuman flips via the snapshot subscription — nothing else to do.
      } catch (err) {
        console.warn(`[verifyHuman fallback] attempt ${attemptState.count} failed:`, err);
        if (!cancelled && attemptState.count < 4) {
          attemptState.timer = setTimeout(attempt, 2000 * (2 ** (attemptState.count - 1)));
        }
      }
    };
    attempt();

    return () => {
      cancelled = true;
      if (attemptState.timer) clearTimeout(attemptState.timer);
    };
    // signupSettledTick: refs aren't reactive — re-evaluate once a signup handler
    // finishes so the fallback isn't permanently skipped for a legitimate signup.
  }, [uid, userLoading, firestoreUser?.isHuman, isCustomerOnly, redirectSettled, signupSettledTick, pendingNewIdentity]);

  // ──────────────────────────────────────────────────────────────────────────
  // DECISION TREE — one effect, one clear path
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;                         // no user → show login form
    if (signupInProgressRef.current) return;  // signup handler owns the flow
    if (pendingNewIdentity) return;         // ask-first choice pending — hold routing
    if (authLoading || userLoading || clientLoading) return; // still loading
    // A disowned shell (declinedAsDuplicateOf, no client role) has a client doc the
    // backend provisioned, which would route it into completeSignup as a second
    // account. Hold: the resume ask-first effect parks it for an explicit choice.
    const heldRoles = Array.isArray(firestoreUser?.roles) ? firestoreUser.roles : [];
    if (firestoreUser?.declinedAsDuplicateOf && !heldRoles.includes('client')) return;

    const decision = resolveNextRoute({ emailVerified, firestoreUser, client, queryParams });

    if (!decision) {
      // null = waiting for client doc / human check. Not caused by useClient — uses useClientForOwner + useUser.
      if (isLocalHost) {
        console.log('[login] Waiting for client — isHuman:', !!firestoreUser?.isHuman, 'emailVerified:', emailVerified, 'client:', !!client);
      }
      if (uid && emailVerified && firestoreUser && firestoreUser.isHuman !== true) {
        setMessage(
          'Verifying device security (reCAPTCHA)… Poor Wi‑Fi, VPNs, or ad blockers often cause this to hang. Try refresh.',
        );
      } else if (uid && emailVerified && firestoreUser?.isHuman === true && !client && !clientLoading) {
        setMessage(
          'Loading your account… If this lasts more than a minute, check your connection and refresh.',
        );
      } else {
        setMessage('Setting up your account…');
      }
      return;
    }

    if (decision.message === 'waiting_for_human') {
      setMessage('Verifying your identity…');
      return;
    }

    if (decision.message === 'customer_only') {
      setIsCustomerOnly(true);
      return;
    }

    console.log('[login] decision: %s (client=%s)', decision.route, client?.id || 'none');

    if (decision.clearSession) sessionClearRedirectParams();
    setMessage(decision.message);
    setRedirectOnLogin(decision.route);

    // Tracking (only when client exists, and only once per authenticated session —
    // this effect re-runs on every auth/user/client snapshot, so an unguarded track
    // fires per Firestore update and inflates the counts massively).
    if (client && signInTrackedRef.current !== uid) {
      signInTrackedRef.current = uid;
      const isTest = isLocalHost;
      const tracker = new TrackerService({ isTest });
      const isNewSignup = authUser?.metadata
        ? authUser.metadata.creationTime === authUser.metadata.lastSignInTime : false;
      if (client.displayName && client.email) {
        tracker.mixPanelIdentify(uid, client.id, client.displayName, client.email);
      }
      tracker.mixpanelTrack('client_sign_in', {
        distinct_id: uid, clientId: client.id, isNewSignup,
        redirect: !!queryParams.redirect, redirectUrl: decision.route,
      });
      if (!isTest) {
        tracker.googleTrack('start_claim_clicked', {
          userId: uid, clientId: client.id, isNewSignup,
          redirect: !!queryParams.redirect, redirectUrl: decision.route,
        });
      }
    }
  }, [uid, authLoading, userLoading, clientLoading, emailVerified, firestoreUser, client, queryParams, authUser, isLocalHost, signupSettledTick, pendingNewIdentity]);

  // ── "Existing means existing" guard ──
  // The lookup told us this identifier already has an account, yet the provider the
  // user just picked MINTED a brand-new auth user (additionalUserInfo.isNewUser) —
  // i.e. a different Google/Apple/phone than the one on the account. Completing this
  // would create the very duplicate we're trying to prevent. Delete the orphan and
  // send them back to their real sign-in methods. (No GCIP/blocking function needed;
  // the just-created user can delete itself, and onDeleteAuthUser tombstones its doc.)
  // inSignupFlowOverride: signup intent persisted across a signInWithRedirect
  // round-trip (the page unload wipes isSignup/proceedToSignup — see authFlow.js).
  // askFirst (DEFAULT, every surface): a brand-new auth user is parked for an explicit
  // choice instead of deleted. Deleting first cost real signups and minted a fresh uid
  // per retry; asking keeps the duplicate-account protection as a labelled decision
  // ("I already have an account") while letting a genuinely new person through in one
  // pass. Only rejectNewIdentity passes askFirst:false — that IS the delete path.
  const abortIfAccidentalNewUser = useCallback(async (authResult, {
    inSignupFlowOverride = false, askFirst = true, via = 'signup', skipLookup = false, countryCode = null,
  } = {}) => {
    const inSignupFlow = isSignup || proceedToSignup || inSignupFlowOverride;
    const isNewUser = !!(authResult && authResult.additionalUserInfo && authResult.additionalUserInfo.isNewUser);
    const newUser = authResult && authResult.user;
    // Login is existence-gated: a brand-new OAuth/phone user means the credential did
    // NOT exist yet, so it must never become a silent signup. Only an explicit signup
    // flow may create. Delete the orphan and route the user through the typo-check.
    if (!isNewUser || !newUser) return false;
    const identifier = newUser.email || newUser.phoneNumber || null;

    if (inSignupFlow) {
      // An explicit sign-up proceeds — unless we can positively find an account the
      // person already has, in which case they get the found-account screen with
      // "Create a new account anyway" still one tap away. The join landing's "Just
      // sign up." link and bare /client/signup both land here. A resumed choice
      // (skipLookup, set only by confirmNewIdentity) has already been made and is
      // never re-asked. A redirect return with persisted sign-up intent is NOT a
      // choice — the person has only tapped Google/Apple on a sign-up door — so
      // it still gets the lookup, like the popup path.
      if (skipLookup || !askFirst || !identifier || !recaptchaExpected()) return false;
      const match = await findExistingAccountFor(newUser);
      if (!match) return false;
      setPendingNewIdentity({ authResult, via, checking: false, match, countryCode });
      return true;
    }

    if (askFirst) {
      // Park, don't delete: the choice UI resumes via confirmNewIdentity (treat as
      // consented signup, same auth user — no second provider round-trip) or
      // rejectNewIdentity (the standard delete + typo-check). The decision tree,
      // verifyHuman and self-heal all hold while this is pending, so the parked uid
      // can't be routed or promoted client-side before the user decides.
      const checking = !!identifier && recaptchaExpected();
      setPendingNewIdentity({ authResult, via, checking, countryCode });
      if (checking) checkParkedIdentityForExistingAccount(authResult);
      return true;
    }

    const attemptedIdentifier = identifier || '';
    // NO CLIENT-SIDE DELETION (2026-08-13). Every lockout class this month —
    // July's lost signups, the veteran lockouts, the Kim loop, Amanda's orphaned
    // client — traced back to deleting auth users as flow control. States, not
    // deletes: "not my account" signs out and the uid SURVIVES, with its history,
    // its Apple-given name, and anything it owns. A returning shell resumes via
    // the ask-first prompt on the same uid; cold bare shells are the future
    // janitor's job (server-side, ownership-checked), never the browser's.
    try { await firebase.auth().signOut(); } catch (_) { /* noop */ }
    setShowPhone(false);
    setIdentityEntry(null);
    setAuthError(null);
    // Same "make sure this isn't a typo" gate as an email/mobile miss — flagged as a
    // provider bounce so the copy can speak to Google/Apple rather than spelling.
    setLookupResult({ status: 'not_found', identifier: attemptedIdentifier, providers: [], viaProvider: true });
    return true;
  }, [isSignup, proceedToSignup, checkParkedIdentityForExistingAccount]);

  // ── Signup handler (FirebaseLogin callback) ──
  const handleNewUserSignup = useCallback(async (authResult, error, { inSignupFlowOverride = false, skipLookup = false } = {}) => {
    if (error) {
      console.error('Error during signup:', error);
      setMessage('Signup error, please try again.');
      await firebase.auth().signOut();
      return;
    }
    const user = authResult?.user;
    if (!user) {
      console.error('No user in authResult');
      setMessage('No user found');
      return;
    }

    signupInProgressRef.current = true;
    if (await abortIfAccidentalNewUser(authResult, { inSignupFlowOverride, via: 'signup', skipLookup })) {
      endSignupFlow();
      return;
    }
    const { uid: signupUid, displayName } = user;
    setMessage(`Welcome${displayName ? `, ${displayName}` : ''}!`);

    try {
      if (signupUid) {
        const isNewUser = !!(authResult.additionalUserInfo && authResult.additionalUserInfo.isNewUser);
        const grantRole = isNewUser || !(await isDisownedShell(signupUid));
        const signupUpdates = {
          platform: 'web',
          ...(grantRole ? { roles: firebase.firestore.FieldValue.arrayUnion('client') } : {}),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          lastAuthAt: firebase.firestore.FieldValue.serverTimestamp(),
          lastOriginReason: originContext.reason || ORIGIN_REASON_MAP.WEBSITE,
          lastOriginPath: originContext.path || '/client/login',
          lastOriginParams: originContext.params || {},
        };
        const { redirect: redir, redirectRoute: redirRoute } = queryParams;
        if (redir) signupUpdates.pendingRedirect = redir;
        else if (redirRoute) signupUpdates.pendingRedirectRoute = redirRoute;
        if (loginContext.tagId) signupUpdates.signupTagId = loginContext.tagId;
        if (loginContext.templateTagId) signupUpdates.signupTemplateTagId = loginContext.templateTagId;
        if (loginContext.teamId) signupUpdates.signupTeamId = loginContext.teamId;
        if (navigator.language) signupUpdates.locale = navigator.language;
        if (displayName) signupUpdates.displayName = displayName;
        if (user.email) signupUpdates.email = user.email;
        await firebase.firestore().collection('users').doc(signupUid)
          .set(signupUpdates, { merge: true });
      }

      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      const token = await window.grecaptcha.execute(GOOG_RECAPTCHA_SITE_KEY, { action: 'signup' });
      if (!token) throw new Error('reCAPTCHA token not received');

      const verifyHuman = firebase.functions().httpsCallable('userService-humanHandler-verifyHuman');
      const result = await verifyHuman({ token, action: 'signup', platform: 'web' });
      if (!result.data?.success) throw new Error('Verify human failed');

      endSignupFlow();
      // hooks will re-fire the decision tree automatically
    } catch (err) {
      endSignupFlow();
      console.error('Signup Error:', err);
      setMessage('Signup error, please try again.');
      alert('Signup error, please try again.');
    }
  }, [originContext, queryParams, loginContext, abortIfAccidentalNewUser]);

  // ── Handler for self-verifying providers (Phone, Apple).
  // These providers are inherently verified — no reCAPTCHA human check needed.
  // The backend Cloud Function auto-sets isHuman=true for phone/apple providers.
  // We still write the client role and origin metadata so the decision tree can proceed.
  const handleVerifiedAuthSuccess = useCallback(async (authResult, {
    countryCode, inSignupFlowOverride = false, skipLookup = false,
  } = {}) => {
    const user = authResult && authResult.user;
    if (!user) return;
    signupInProgressRef.current = true;
    if (await abortIfAccidentalNewUser(authResult, {
      inSignupFlowOverride, via: 'verified', skipLookup, countryCode,
    })) {
      endSignupFlow();
      return;
    }
    try {
      // A disowned shell (the person said "use my existing account" once) signing
      // in again by the same number/provider must not be re-granted the client role
      // here, or the backend provisions it as a second account before the resume
      // ask-first effect can park it. Everything else about the visit is recorded.
      const isNewUser = !!(authResult.additionalUserInfo && authResult.additionalUserInfo.isNewUser);
      const grantRole = isNewUser || !(await isDisownedShell(user.uid));
      const updates = {
        platform: 'web',
        ...(grantRole ? { roles: firebase.firestore.FieldValue.arrayUnion('client') } : {}),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        lastAuthAt: firebase.firestore.FieldValue.serverTimestamp(),
        lastOriginReason: originContext.reason || ORIGIN_REASON_MAP.WEBSITE,
        lastOriginPath: originContext.path || '/client/login',
        lastOriginParams: originContext.params || {},
      };
      const { redirect: redir, redirectRoute: redirRoute } = queryParams;
      if (redir) updates.pendingRedirect = redir;
      else if (redirRoute) updates.pendingRedirectRoute = redirRoute;
      if (loginContext.tagId) updates.signupTagId = loginContext.tagId;
      if (loginContext.templateTagId) updates.signupTemplateTagId = loginContext.templateTagId;
      if (loginContext.teamId) updates.signupTeamId = loginContext.teamId;
      if (navigator.language) updates.locale = navigator.language;
      if (user.displayName) updates.displayName = user.displayName;
      if (user.email) updates.email = user.email;
      if (user.phoneNumber) updates.phoneNumber = user.phoneNumber;
      if (countryCode) updates.signupCountry = countryCode;
      await firebase.firestore().collection('users').doc(user.uid).set(updates, { merge: true });
    } catch (err) {
      console.warn('[login] verified auth metadata write failed:', err?.message);
    } finally {
      endSignupFlow();
      // onAuthStateChanged fires → decision tree re-runs automatically
    }
  }, [originContext, queryParams, loginContext, abortIfAccidentalNewUser]);

  // ── Ask-first choice resolution ──
  // "Continue as a new account": resume the ORIGINAL handler with signup intent — same
  // auth user, no second provider round-trip. The backend may already have
  // provisioned a client during the pause (it fires within ~1s of the uid existing);
  // that's fine — the handler's writes are idempotent and the decision tree picks
  // the client up on settle.
  const confirmNewIdentity = useCallback(() => {
    const pending = pendingNewIdentity;
    if (!pending) return;
    setPendingNewIdentity(null);
    if (pending.via === 'resume') {
      // No authResult to resume (the original choice was lost to a reload) — the
      // consented role write IS the completion; the backend provisions from it and
      // the decision tree picks the client up on settle.
      firebase.firestore().collection('users').doc(uid)
        .set({
          roles: firebase.firestore.FieldValue.arrayUnion('client'),
          // A disowned shell the person now explicitly continues with is theirs again.
          declinedAsDuplicateOf: null,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }, { merge: true })
        .catch(err => console.warn('[login] resume-consent role write failed:', err?.message));
      return;
    }
    if (pending.via === 'verified') {
      handleVerifiedAuthSuccess(pending.authResult, {
        inSignupFlowOverride: true, skipLookup: true, countryCode: pending.countryCode || undefined,
      });
    } else {
      handleNewUserSignup(pending.authResult, null, { inSignupFlowOverride: true, skipLookup: true });
    }
  }, [pendingNewIdentity, uid, handleVerifiedAuthSuccess, handleNewUserSignup]);

  // "I have another account": the standard strict abort — delete the parked orphan
  // (onDeleteAuthUser tombstones it and pause-sweeps any client the backend minted
  // meanwhile) and fall through to the typo-check chooser.
  const rejectNewIdentity = useCallback(async () => {
    const pending = pendingNewIdentity;
    if (!pending) return;
    setPendingNewIdentity(null);
    signupInProgressRef.current = true;
    if (pending.via === 'resume') {
      // NO CLIENT-SIDE DELETION (2026-08-13) — see the abort path. Sign out;
      // the uid survives and resumes on its next sign-in.
      try { await firebase.auth().signOut(); } catch (_) { /* noop */ }
      endSignupFlow();
      return;
    }
    // askFirst:false is REQUIRED here — this is the delete path. Parking is the
    // default now, so omitting it would re-park the same result forever.
    await abortIfAccidentalNewUser(pending.authResult, { askFirst: false });
    endSignupFlow();
  }, [pendingNewIdentity, abortIfAccidentalNewUser, endSignupFlow]);

  // "Sign in with my existing account": the check found one. Stop being signed in
  // as the parked identity (states, not deletes — the uid survives) and drop the
  // person onto that account's real sign-in methods, exactly as if they had typed
  // the identifier into the email/mobile field.
  const useExistingAccountInstead = useCallback(async () => {
    const pending = pendingNewIdentity;
    if (!pending || !pending.match) return;
    const { match } = pending;
    setPendingNewIdentity(null);
    signupInProgressRef.current = true;
    // Mark the shell as disowned BEFORE signing out (owner-only write). The server
    // lookup then looks past it — otherwise the shell's own Google credential would
    // shadow the real account the next time this email is typed — and a later
    // sign-in to the shell re-asks instead of auto-completing it as a duplicate.
    const shellUid = (pending.authResult && pending.authResult.user && pending.authResult.user.uid) || uid;
    if (shellUid) {
      try {
        await firebase.firestore().collection('users').doc(shellUid).set({
          declinedAsDuplicateOf: match.identifier,
          declinedAsDuplicateAt: firebase.firestore.FieldValue.serverTimestamp(),
        }, { merge: true });
      } catch (err) {
        console.warn('[login] disowned-shell flag write failed:', err?.message);
      }
    }
    try { await firebase.auth().signOut(); } catch (_) { /* noop */ }
    // The scoped-providers login only renders in login mode: leave sign-up mode,
    // carrying the redirect, the same way the mode switcher does.
    if (isSignup) {
      const { redirect, redirectRoute } = queryParams;
      const qs = redirect ? `?redirect=${redirect}` : (redirectRoute ? `?redirectRoute=${redirectRoute}` : '');
      history.push(`/client/login${qs}`);
      setIsSignup(false);
    }
    setAuthError(null);
    setShowAllProviders(false);
    setIdentityEntry(null);
    setLookupResult({
      status: match.status,
      providers: match.providers,
      hasPhoneCredential: !!match.hasPhoneCredential,
      identifier: match.identifier,
    });
    const enteredPhone = typeof match.identifier === 'string' && match.identifier.startsWith('+');
    const onlyPhone = (match.providers || []).length === 1 && match.providers[0] === 'phone';
    setShowPhone(enteredPhone || onlyPhone);
    setShowEmail(!enteredPhone);
    endSignupFlow();
  }, [pendingNewIdentity, endSignupFlow, uid, isSignup, queryParams, history]);

  // ── Redirect-flow completion (mobile Google/Apple) ──
  // On mobile the OAuth buttons use signInWithRedirect (popups are blocked there —
  // see utils/authFlow). The round-trip lands back here with the result pending;
  // consume it once and route into the SAME handlers the popup flow uses, so the
  // anti-duplicate abort and metadata writes behave identically on both flows.
  const redirectResultConsumedRef = useRef(false);
  useEffect(() => {
    if (redirectResultConsumedRef.current) return;
    redirectResultConsumedRef.current = true;
    const auth = firebase.auth();
    if (typeof auth.getRedirectResult !== 'function') { // test/older mocks
      setRedirectSettled(true);
      return;
    }
    // Signup intent persisted before the redirect unload wiped isSignup/proceedToSignup.
    // Consume-once: a stale flag must never suppress the abort on a later genuine login.
    const pendingIntent = consumePendingAuthIntent();
    const inSignupFlowOverride = !!(pendingIntent && pendingIntent.inSignupFlow);
    // Spotty-network guard: getRedirectResult can hang on a dying connection, and
    // redirectSettled gates the verifyHuman fallback — the page must become operable
    // even if that promise never resolves. Handlers still run whenever it does.
    const settleTimer = setTimeout(() => setRedirectSettled(true), 15000);
    auth.getRedirectResult()
      .then((result) => {
        if (!result || !result.user) return; // no pending redirect — the common case
        const providerId = (result.additionalUserInfo && result.additionalUserInfo.providerId)
          || (result.credential && result.credential.providerId) || '';
        if (providerId === 'apple.com') {
          handleVerifiedAuthSuccess(result, { inSignupFlowOverride });
        } else if (providerId === 'google.com') {
          handleNewUserSignup(result, null, { inSignupFlowOverride });
        }
      })
      .catch((err) => {
        console.warn('[login] redirect sign-in failed:', err && err.message);
        setAuthError((err && err.message) || 'Sign-in failed, please try again.');
      })
      .finally(() => {
        clearTimeout(settleTimer);
        setRedirectSettled(true);
      });
  }, []);

  // ── Toggle login/signup ──
  const handleToggleMode = useCallback((toSignup) => {
    const { redirect, redirectRoute } = queryParams;
    let newPath = toSignup ? '/client/signup' : '/client/login';
    let qs = '';
    if (redirect) qs = `?redirect=${redirect}`;
    else if (redirectRoute) qs = `?redirectRoute=${redirectRoute}`;
    history.push(newPath + qs);
    setIsSignup(toSignup);
    // Restart the flow when switching login/signup mode — including the sign-up
    // reached from "None of these — create a new account", or "Log in" is dead.
    setProceedToSignup(false);
    setScopedMethod(null);
    setLookupResult(null);
    setShowAllProviders(false);
    setShowPhone(false);
    setShowEmail(true);
    setAuthError(null);
    setIdentityEntry(null);
    setSignupMoreOptions(false);
  }, [queryParams, history]);

  // ── Back to the initial Step-1 chooser (login: Google/Apple/email row) ──
  const resetIdentityFlow = useCallback(() => {
    setScopedMethod(null);
    setLookupResult(null);
    setShowAllProviders(false);
    setShowPhone(false);
    setShowEmail(true);
    setAuthError(null);
    setProceedToSignup(false);
    setIdentityEntry(null);
    setSignupMoreOptions(false);
  }, []);

  // ── "Use a different email or number" — reopen the entry field itself, in the mode
  // the person used last (an E.164 identifier means they typed a mobile) ──
  const reopenEmailEntry = useCallback(() => {
    const usedPhone = !!(lookupResult && typeof lookupResult.identifier === 'string'
      && lookupResult.identifier.startsWith('+'));
    const last = usedPhone ? nationalFromE164(lookupResult.identifier) : null;
    setRetryCountry((last && last.country) || null);
    setLookupResult(null);
    setShowPhone(false);
    setShowEmail(true);
    setAuthError(null);
    setIdentityEntry(usedPhone ? 'phone' : 'email');
  }, [lookupResult]);

  // ── Cancel / sign out ──
  const cancelLogin = useCallback(async () => {
    await firebase.auth().signOut();
    history.push('/');
  }, [history]);

  // ── Derived loading: spinner when subscriptions haven't settled ──
  // Do not block the signed-out login form on authLoading — first onAuthStateChanged can lag;
  // a full-page spinner hid FirebaseUI and contributed to "widget element not found" / blank login.
  const subsLoading = !!uid && (userLoading || clientLoading);
  const awaitingClient = !!uid && emailVerified && !client && !isCustomerOnly && !redirectOnLogin;
  const loading = subsLoading || awaitingClient || (!!uid && !emailVerified && !redirectOnLogin);

  // Stall detector: if the spinner persists >20s, show a reload CTA instead of
  // spinning forever (see the waiting_for_human lockout this replaces).
  useEffect(() => {
    if (!loading) {
      setLoadingStalled(false);
      return undefined;
    }
    const t = setTimeout(() => setLoadingStalled(true), 20000);
    return () => clearTimeout(t);
  }, [loading]);

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  if (redirectOnLogin) {
    return <Redirect to={redirectOnLogin} />;
  }

  const hasContext = !loginContext.loading && loginContext.type !== 'default';
  const parkedIdentifier = pendingNewIdentity
    ? (pendingNewIdentity.authResult?.user?.email
      || pendingNewIdentity.authResult?.user?.phoneNumber
      || authUser?.email || authUser?.phoneNumber || 'your account')
    : null;
  const parkedMatch = (pendingNewIdentity && pendingNewIdentity.match) || null;
  // A claim/join landing knows the venue's payout currency, which says more about
  // where its staff are than the device locale does (USD → US, GBP → GB).
  const landingCountryHint = countryForCurrency(loginContext.currency);
  // The sign-up button names the finish: the team, the tag, or just the account.
  const signupCtaLabel = loginContext.type === 'join'
    ? `Join ${loginContext.teamName || 'the team'}`
    : (loginContext.type === 'claim' ? 'Claim my tag' : 'Create my account');
  // Mobile + code → about you → (join | claim). The root door has no third step.
  const signupStepsTotal = loginContext.type === 'default' ? 2 : 3;
  // Show login form only when no user. Once uid exists, we're past sign-in — show spinner/redirect, never login.
  const showLoginUI = !isCustomerOnly && !uid;

  return (
    <div
      className="d-flex flex-column flex-grow-1"
      style={{
        backgroundColor: 'black',
        width: '100%',
        minHeight: 0,
        flex: '1 1 auto',
        overflowY: 'auto',
      }}
    >
      {hasContext && loginContext.type !== 'join'
        && <LoginContextBanner loginContext={loginContext} locStrings={locStrings} />}

      <div className="container-fluid p-0 m-0 d-flex flex-grow-1 align-items-start justify-content-center">
      <div
        className="row w-100 justify-content-center pb-4 px-3"
        style={{
          paddingTop: 'max(2rem, calc(1.25rem + env(safe-area-inset-top, 0px)))',
        }}
      >

        <div
          className="col-12 d-flex flex-column align-items-stretch login-column"
          style={{ width: '100%' }}
        >
          {pendingNewIdentity ? (
            // ── Ask-first: a provider sign-in minted a NEW auth user. Never delete it
            // silently — say what happened and let the person choose. The majority case
            // on every surface is a genuinely new person (existing users are usually
            // already signed in, or their provider resolves to their real account), so
            // continuing is the primary action and switching accounts the quiet
            // secondary. The duplicate-account protection survives as that explicit
            // "I already have an account" choice.
            <div style={{ padding: '0 24px' }}>
              {pendingNewIdentity.checking ? (
                // Looking up the new identity's email/phone — a second or so. Only
                // entered when reCAPTCHA is available, and capped at 6s.
                <>
                  <Text style={{ color: '#fff', fontSize: 20, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    One moment…
                  </Text>
                  <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginBottom: 16 }}>
                    Checking whether{' '}
                    <span style={{ color: '#fff' }}>{parkedIdentifier}</span>
                    {' '}is already on a Striketip account.
                  </Text>
                </>
              ) : parkedMatch ? (
                // ── Found one: this is the duplicate-account moment. Lead with the
                // existing account and its real sign-in methods; a new account is the
                // quiet secondary, still one tap away for the person who really wants it.
                <>
                  <Text style={{ color: '#fff', fontSize: 20, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    We found your account
                  </Text>
                  <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginBottom: 4 }}>
                    <span style={{ color: '#fff' }}>{parkedIdentifier}</span>
                    {' '}is already on a Striketip account that signs in with{' '}
                    {describeProviders(parkedMatch.providers)}.
                  </Text>
                  <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginBottom: 16 }}>
                    {parkedMatch.status === 'account_no_credential'
                      ? 'It is saved there as a contact detail rather than a sign-in method, so sign in the way you did before — you can add this sign-in to that account afterwards.'
                      : 'Sign in that way to pick up where you left off — no need for a second account.'}
                  </Text>
                  <button
                    type="button"
                    onClick={useExistingAccountInstead}
                    style={{
                      width: '100%', height: 44, background: '#ff0098', border: 'none',
                      borderRadius: 4, color: '#fff', fontSize: 15, fontWeight: 600,
                      cursor: 'pointer', marginBottom: 8,
                    }}
                  >
                    Sign in with my existing account
                  </button>
                  <button
                    type="button"
                    onClick={confirmNewIdentity}
                    style={{
                      width: '100%', background: 'none', border: 'none', color: '#888',
                      fontSize: 13, cursor: 'pointer', padding: '8px 0',
                    }}
                  >
                    {isJoinTeamLanding ? 'Join with a new account anyway' : 'Create a new account anyway'}
                  </button>
                </>
              ) : (
              <>
              <Text style={{ color: '#fff', fontSize: 20, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Looks like you&apos;re new here
              </Text>
              <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginBottom: 4 }}>
                Signed in as{' '}
                <span style={{ color: '#fff' }}>{parkedIdentifier}</span>
                {' '}— no existing striketip account found for it.
              </Text>
              <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginBottom: 16 }}>
                {isPartnerConnectLanding
                  ? 'Create your account with it to join the team — this also connects your partner ID to your team membership.'
                  : isJoinTeamLanding
                    ? 'Create your account with it to accept your team invite.'
                    : isClaimTagLanding
                      ? 'Create your account with it to claim your tag.'
                      : 'Create your account with it to get started.'}
              </Text>
              <button
                type="button"
                onClick={confirmNewIdentity}
                style={{
                  width: '100%', height: 44, background: '#ff0098', border: 'none',
                  borderRadius: 4, color: '#fff', fontSize: 15, fontWeight: 600,
                  cursor: 'pointer', marginBottom: 8,
                }}
              >
                {isJoinTeamLanding ? 'Join with this account' : 'Continue with this account'}
              </button>
              <button
                type="button"
                onClick={rejectNewIdentity}
                style={{
                  width: '100%', background: 'none', border: 'none', color: '#888',
                  fontSize: 13, cursor: 'pointer', padding: '8px 0',
                }}
              >
                I already have an account — use a different sign-in
              </button>
              </>
              )}
            </div>
          ) : isCustomerOnly ? (
            <CustomerOnlyNotice onSignInDifferent={cancelLogin} />
          ) : loading ? (
            <div className="text-center px-2 mt-3" style={{ height: '100%' }}>
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div
                  className="spinner-border"
                  role="status"
                  style={{ width: '5rem', height: '5rem', color: '#ff0098' }}
                >
                  <span className="visually-hidden"></span>
                </div>
              </div>
              {message && (
                <p className="text-white-50 mt-3" style={{ fontSize: '1rem' }}>{message}</p>
              )}
              {loadingStalled && (
                <>
                  <p className="text-white-50 mt-3" style={{ fontSize: '1rem' }}>
                    This is taking longer than it should. Poor Wi‑Fi, VPNs, or content
                    blockers can interfere — reloading usually fixes it.
                  </p>
                  <button
                    className="btn btn-outline-light m-1"
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </button>
                </>
              )}
              <button className="btn btn-primary m-3" onClick={cancelLogin}>
                Cancel
              </button>
            </div>
          ) : (
            <LoginHeading
              loginContext={loginContext}
              locStrings={(lookupResult && lookupResult.status !== 'not_found' && !showAllProviders) ? {
                ...locStrings,
                subtitle: { ...locStrings.subtitle, login: 'We found your account.' },
                subsubtitle: { ...locStrings.subsubtitle, login: '' },
              } : locStrings}
              // Reflect sign-up in the heading once the user chooses to create a new account
              // from the not-found screen, so it's not "Welcome back" over Sign-up buttons.
              isSignup={isSignup || proceedToSignup}
              onSignup={() => handleToggleMode(true)}
              step={(isSignup || proceedToSignup) ? 1 : null}
              of={signupStepsTotal}
            />
          )}

          {showLoginUI && (() => {
            // ── Step 1 dispatch (in order): typo-check → signup chooser → scoped
            // providers (login, account identified) → email/mobile entry → the
            // all-methods login chooser. ──
            const inSignup = isSignup || proceedToSignup;
            const isNotFound = !!lookupResult && lookupResult.status === 'not_found';

            // On a miss, don't drop the user straight onto sign-up buttons — one tap there
            // mints a duplicate account after a typo or a forgotten email/number. Show an
            // interstitial that nudges them to re-check first; creating a new account
            // requires an explicit second step (setProceedToSignup).
            if (isNotFound && !proceedToSignup) {
              const viaProvider = !!(lookupResult && lookupResult.viaProvider);
              const typed = formatE164ForDisplay((lookupResult && lookupResult.identifier) || '');
              return (
                <div style={{ padding: '0 24px' }}>
                  <Text style={{ color: '#fff', fontSize: 18, fontWeight: 600, display: 'block' }}>
                    {viaProvider ? 'No account found for that sign-in' : 'We couldn’t find your details'}
                  </Text>
                  {typed && (
                    <Text style={{ color: '#888', fontSize: 13, display: 'block', marginTop: 4 }}>
                      {viaProvider ? 'Signed in as' : 'You entered'} <span style={{ color: '#ddd' }}>{typed}</span>
                    </Text>
                  )}
                  <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginTop: 16 }}>
                    Before creating a new account, please check:
                  </Text>
                  <ul style={{ color: '#bbb', fontSize: 13, margin: '8px 0 0', paddingLeft: 18 }}>
                    {viaProvider ? (
                      <>
                        <li>Did you use a different Google or Apple account last time?</li>
                        <li>Did you sign up with an email or mobile number instead?</li>
                        <li>New here? Create an account below.</li>
                      </>
                    ) : (
                      <>
                        <li>Is your email or mobile number spelled correctly?</li>
                        <li>Did you sign up before with a different email or number?</li>
                        <li>
                          If you added this email or number recently, make sure you verified it for
                          login on your Security page.
                        </li>
                      </>
                    )}
                  </ul>

                  <button
                    type="button"
                    onClick={viaProvider ? resetIdentityFlow : reopenEmailEntry}
                    style={{
                      width: '100%', height: 44, marginTop: 20, background: '#ff0098',
                      border: 'none', borderRadius: 4, color: '#fff', fontSize: 15,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => setProceedToSignup(true)}
                    style={{
                      width: '100%', background: 'none', border: 'none', color: '#888',
                      fontSize: 13, cursor: 'pointer', marginTop: 14, padding: 0,
                    }}
                  >
                    None of these — create a new account
                  </button>
                </div>
              );
            }

            // Signup: the mobile number IS the credential (phone-first). Google, Apple
            // and email stay available under "Other ways to sign up" — each of those
            // still needs a verified mobile later, so for this audience they are the
            // longer road, not the shorter one. A number the person already typed on
            // the login lookup is carried in, never typed twice.
            if (inSignup) {
              const lookupId = (lookupResult && typeof lookupResult.identifier === 'string')
                ? lookupResult.identifier : '';
              const signupPrefillEmail = lookupId.includes('@') ? lookupId : '';
              const signupPrefillPhone = lookupId.startsWith('+') ? lookupId : '';
              return (
                // No inner gutter: the field, the button and the heading share one left edge.
                <div className="signup-body">
                  {authError && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="danger" style={{ fontSize: 16 }}>{authError}</Text>
                    </div>
                  )}
                  <PhoneFirstSignup
                    key={signupPrefillPhone || 'fresh'}
                    defaultCountryCode={retryCountry || landingCountryHint}
                    initialPhone={signupPrefillPhone || undefined}
                    ctaLabel={signupCtaLabel}
                    onSuccess={(authResult, { countryCode } = {}) => (
                      handleVerifiedAuthSuccess(authResult, { countryCode })
                    )}
                  />
                  {signupMoreOptions ? (
                    <div style={{ marginTop: 20 }}>
                      <Text style={{ color: '#888', fontSize: 16, display: 'block', marginBottom: 8 }}>
                        Other ways to sign up
                      </Text>
                      <div style={{ marginBottom: 8 }}>
                        <ClientGoogleLogin isSignup handler={(a, e) => handleNewUserSignup(a, e)} />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <ClientAppleLogin isSignup onSuccess={handleVerifiedAuthSuccess} />
                      </div>
                      <div>
                        <ClientEmailLogin
                          initialEmail={signupPrefillEmail}
                          isSignup
                          handler={(a, e) => handleNewUserSignup(a, e)}
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSignupMoreOptions(true)}
                      style={{ ...signupTextLink, color: '#bbb', marginTop: 8 }}
                    >
                      Other ways to sign up
                    </button>
                  )}
                </div>
              );
            }

            // Login, no account identified yet: the mobile or email entry field once chosen,
            // else the all-methods chooser (Google, Apple, mobile, email) as Step 1.
            if (!lookupResult) {
              if (identityEntry) {
                return (
                  <ClientIdentityFirst
                    key={identityEntry}
                    isSignup={false}
                    mode={identityEntry}
                    onSwitchMode={() => setIdentityEntry(identityEntry === 'phone' ? 'email' : 'phone')}
                    defaultCountryCode={retryCountry || landingCountryHint}
                    onResolved={(r) => {
                      setAuthError(null);
                      setLookupResult(r);
                      // Open the method matching what they typed; collapse the other.
                      const enteredPhone = typeof r.identifier === 'string' && r.identifier.startsWith('+');
                      const onlyPhone = (r.providers || []).length === 1 && r.providers[0] === 'phone';
                      setShowPhone(enteredPhone || onlyPhone);
                      setShowEmail(!enteredPhone);
                    }}
                    onSkip={() => setIdentityEntry(null)}
                  />
                );
              }
              return (
                <>
                  {authError && (
                    <div style={{ padding: '0 24px', marginBottom: 8 }}>
                      <Text type="danger" style={{ fontSize: 13 }}>{authError}</Text>
                    </div>
                  )}
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <ClientGoogleLogin isSignup={false} handler={(a, e) => handleNewUserSignup(a, e)} />
                  </div>
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <ClientAppleLogin isSignup={false} onSuccess={handleVerifiedAuthSuccess} />
                  </div>
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => { setRetryCountry(null); setIdentityEntry('phone'); }}
                      style={{
                        width: '100%', height: 44, background: '#1a1a1a',
                        border: '1px solid #555', borderRadius: 4, color: '#fff',
                        fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      Continue with mobile
                    </button>
                  </div>
                  <div style={{ padding: '0 24px' }}>
                    <button
                      type="button"
                      onClick={() => { setRetryCountry(null); setIdentityEntry('email'); }}
                      style={{
                        width: '100%', height: 44, background: '#1a1a1a',
                        border: '1px solid #555', borderRadius: 4, color: '#fff',
                        fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      Continue with email
                    </button>
                  </div>
                </>
              );
            }

            const scoped = !showAllProviders && !isNotFound && !!lookupResult;

            // Scoped to a real account that resolved with NO usable sign-in method (e.g. its
            // only credential was removed). Do NOT fall back to showing every provider — that
            // invites a sign-in attempt that can't succeed ("No account found for that email").
            // Route to recovery instead. (The backend lookup already returns not_found for
            // anon/closed husks, so this is the rare method-less-but-real case.)
            if (scoped && !(lookupResult.providers || []).length) {
              return (
                <div style={{ padding: '0 24px' }}>
                  <Text style={{ color: '#fff', fontSize: 18, fontWeight: 600, display: 'block' }}>
                    This account has no active sign-in method
                  </Text>
                  <Text style={{ color: '#bbb', fontSize: 14, display: 'block', marginTop: 12 }}>
                    We found your account, but there’s no way to sign in to it right now. Try a
                    different email or number, or contact support to restore access.
                  </Text>
                  <button
                    type="button"
                    onClick={reopenEmailEntry}
                    style={{
                      width: '100%', height: 44, marginTop: 20, background: '#ff0098',
                      border: 'none', borderRadius: 4, color: '#fff', fontSize: 15,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Use a different email or number
                  </button>
                </div>
              );
            }

            let providers = (scoped && (lookupResult.providers || []).length)
              ? lookupResult.providers
              : ['google', 'email', 'apple', 'phone'];
            // Scoped to an existing account → "Sign in" wording; create/escape → "Sign up".
            const providerIsSignup = scoped ? false : (isNotFound ? true : isSignup);

            const hasGoogle = providers.includes('google');
            const hasEmail = providers.includes('email');
            const hasApple = providers.includes('apple');
            const hasPhone = providers.includes('phone');
            // Carry the identifier the user already typed into the matching provider so
            // they never retype it: phone → phone step, email → email form.
            const typedId = (lookupResult && typeof lookupResult.identifier === 'string') ? lookupResult.identifier : '';
            const prefillPhone = typedId.startsWith('+') ? typedId : undefined;
            const prefillEmail = typedId.includes('@') ? typedId : '';

            // ── Found-account screen: ONE method open, the rest as "or" links. Two
            // methods stacked read as two steps ("do I need both?"); the account's
            // other methods are alternatives, and the person picked one by typing it. ──
            if (scoped) {
              const firstOf = (list) => list.find((m) => providers.includes(m));
              const matching = (prefillPhone && hasPhone) ? 'phone' : ((prefillEmail && hasEmail) ? 'email' : null);
              const primary = (scopedMethod && providers.includes(scopedMethod))
                ? scopedMethod
                : (matching || firstOf(['google', 'apple', 'email', 'phone']));
              const others = providers.filter((m) => m !== primary && ['google', 'apple', 'email', 'phone'].includes(m));
              const altLabel = {
                google: 'Sign in with Google instead',
                apple: 'Sign in with Apple instead',
                email: 'Use your email instead',
                phone: 'Use your mobile number instead',
              };
              const shown = typedId ? formatE164ForDisplay(typedId) : '';
              const banner = lookupResult.status === 'account_no_credential'
                ? `We found your account, but ${shown || 'that'} isn’t a sign-in method on it yet. Sign in the way you did before; you can add it afterwards on Security.`
                : `This is the account for ${shown || 'you'}. Sign in the way you did before.`;

              return (
                <div className="signup-body">
                  <Text style={{ color: '#ddd', fontSize: 16, display: 'block', marginBottom: 12 }}>{banner}</Text>
                  {authError && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="danger" style={{ fontSize: 16 }}>{authError}</Text>
                    </div>
                  )}
                  {primary === 'google' && (
                    <ClientGoogleLogin isSignup={false} handler={(a, e) => handleNewUserSignup(a, e)} />
                  )}
                  {primary === 'apple' && (
                    <ClientAppleLogin isSignup={false} onSuccess={handleVerifiedAuthSuccess} />
                  )}
                  {primary === 'email' && (
                    <ClientEmailLogin initialEmail={prefillEmail} isSignup={false} handler={(a, e) => handleNewUserSignup(a, e)} />
                  )}
                  {primary === 'phone' && (
                    <ClientPhoneLogin
                      isSignup={false}
                      initialPhone={prefillPhone}
                      defaultCountryCode={landingCountryHint}
                      onSuccess={handleVerifiedAuthSuccess}
                    />
                  )}
                  {others.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#777', fontSize: 14 }}>
                        <span style={{ flex: 1, height: 1, background: '#333' }} />
                        <span>or</span>
                        <span style={{ flex: 1, height: 1, background: '#333' }} />
                      </div>
                      {others.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => { setScopedMethod(m); setAuthError(null); }}
                          style={{ ...signupTextLink, color: '#ddd' }}
                        >
                          {altLabel[m]}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={reopenEmailEntry}
                    style={{ ...signupTextLink, color: '#888', marginTop: 8 }}
                  >
                    Not you? Use a different email or number
                  </button>
                </div>
              );
            }

            let banner = null;
            if (isNotFound) {
              banner = "We couldn't find an account with those details. Create a new one:";
            }

            return (
              <>
                {banner && (
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <Text style={{ color: '#bbb', fontSize: 13 }}>{banner}</Text>
                  </div>
                )}

                {authError && (
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <Text type="danger" style={{ fontSize: 13 }}>{authError}</Text>
                  </div>
                )}

                {hasGoogle && (
                  <div style={{ padding: '0 24px', marginBottom: 8 }}>
                    <ClientGoogleLogin
                      isSignup={providerIsSignup}
                      handler={(a, e) => handleNewUserSignup(a, e)}
                    />
                  </div>
                )}

                {hasEmail && (
                  <div style={{ marginBottom: 8 }}>
                    {showEmail ? (
                      <ClientEmailLogin
                        initialEmail={prefillEmail}
                        isSignup={providerIsSignup}
                        handler={(a, e) => handleNewUserSignup(a, e)}
                      />
                    ) : (
                      <div style={{ padding: '0 24px' }}>
                        <button
                          type="button"
                          onClick={() => setShowEmail(true)}
                          style={{
                            width: '100%', height: 44, background: '#1a1a1a',
                            border: '1px solid #555', borderRadius: 4, color: '#fff',
                            fontSize: 14, cursor: 'pointer',
                          }}
                        >
                          {providerIsSignup ? 'Use email to sign up' : 'Use email to log in'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="client-firebase-login-wrap" style={{ padding: '0 24px' }}>
                  {/* Apple */}
                  {hasApple && (
                    <div style={{ marginTop: 8 }}>
                      <ClientAppleLogin
                        isSignup={providerIsSignup}
                        onSuccess={handleVerifiedAuthSuccess}
                      />
                    </div>
                  )}

                  {/* Phone */}
                  {hasPhone && (
                    <div style={{ marginTop: 8 }}>
                      {showPhone ? (
                        <>
                          <ClientPhoneLogin
                            isSignup={providerIsSignup}
                            initialPhone={prefillPhone}
                            defaultCountryCode={landingCountryHint}
                            onSuccess={handleVerifiedAuthSuccess}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPhone(false)}
                            style={{
                              background: 'none', border: 'none', color: '#888',
                              fontSize: 12, cursor: 'pointer', marginTop: 6, padding: 0,
                            }}
                          >
                            ← Back to other options
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowPhone(true)}
                          style={{
                            width: '100%', height: 44, background: '#1a1a1a',
                            border: '1px solid #555', borderRadius: 4, color: '#fff',
                            fontSize: 14, cursor: 'pointer',
                          }}
                        >
                          {providerIsSignup ? 'Sign up with phone number' : 'Sign in with phone number'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Escape hatch / restart: never trap a returning user behind a bad lookup */}
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={reopenEmailEntry}
                      style={{
                        background: 'none', border: 'none', color: '#888',
                        fontSize: 12, cursor: 'pointer', padding: 0,
                      }}
                    >
                      ← Use a different email or number
                    </button>
                  </div>
                </div>
              </>
            );
          })()}

          {showLoginUI && (
            <LoginModeSwitcher
              isSignup={isSignup || proceedToSignup}
              locStrings={locStrings}
              onToggle={handleToggleMode}
            />
          )}

          {isLocalHost && message && (
            <p className="mt-3 mb-0">
              <Text type="danger">{message}</Text>
            </p>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export function ClientSignupScreen(props) {
  return <ClientLoginScreen {...props} />;
}
